import { useEffect, useMemo, useRef, useState } from "react"
import { Link, useLocation, useNavigate, useOutletContext, useSearchParams } from "react-router-dom"

import moment from "moment/moment"
import Delete from "pages/product/type/delete"
import Filter from "pages/product/type/filter"
import nProgress from "nprogress"
import { useAuth } from "contexts/auth-context"
import { Pagination } from "components"
import { lang } from "config"
import { Pencil, Plus } from "components/icons/outline"
import { usePage } from "contexts/page-context"
import useProductTypeRepositories from "repositories/product-type"
import Table from "components/table"
import axios from "axios"

export default function List() {
    // Logged in user ability
    const { can, isSuper, currentBranch } = useAuth()
    // Configuration
    const { configuration } = usePage()

    // Transition
    const { title, transition, setTransition } = useOutletContext()

    // React router navigate hook
    const navigate = useNavigate()

    // React router location hook
    const location = useLocation()

    // Query parameters
    const [searchParams, setSearchParams] = useSearchParams()

    const searchInput = useRef()

    const page = searchParams.get('page')
    const search = searchParams.get('search')
    const name = searchParams.get('name')
    const from = searchParams.get('from')
    const to = searchParams.get('to')

    const orderBy = searchParams.get('order_by')
    const direction = searchParams.get('direction')
    const [isGroupModalOpen, setIsGroupModalOpen] = useState(false)

    const { data: productTypeData, isLoading: isLoadingProductTypeData, mutate: mutateProductTypeData } = useProductTypeRepositories({
        page,
        search,
        name,
        from,
        to,

        order_by: orderBy,
        direction
    })

    useEffect(() => {
        window.history.replaceState({}, document.title)

        if (transition.reverse) {
            setTransition({ type: 'slide', reverse: false })
        }
    }, [])

    useEffect(() => {
        searchInput.current.focus()
    }, [search])

    // Watch changes on petty cash data and loading state
    useEffect(() => {
        if (search === "") {
            navigate(``, { replace: true })
        }

        if (isLoadingProductTypeData) {
            nProgress.start()
        } else {
            nProgress.done()
        }
    }, [productTypeData, isLoadingProductTypeData])

    // Data filter handlers
    const updateFilter = (value) => {
        setSearchParams(value)
    }

    const removeFilter = () => {
        setSearchParams({})
    }

    // Data sort handlers
    const toggleSort = (value) => {
        if ((direction === null || direction === 'desc') || orderBy !== value) {
            setSearchParams({
                order_by: value,
                direction: 'asc'
            })
        } else {
            setSearchParams({
                order_by: value,
                direction: 'desc'
            })
        }
    }

    // Data list props
    const dataListHeaders = [
        {
            isAction: false,
            content: "Nama",
            sortKey: "name"
        },
        {
            isAction: false,
            content: "Sort Order",
            sortKey: "sort_order",
        },
        ...configuration('hierarchy').find((row) => row.key === 'product-line').value ? [{
            isAction: false,
            content: "Product Line",
            sortKey: "product_line.name"
        }] : [],
        {
            isAction: false,
            content: "Dibuat Pada",
            sortKey: "created_at"
        },
        {
            isAction: false,
            content: "Diubah Pada",
            sortKey: "updated_at"
        },
        {
            isAction: true,
            content: null
        }
    ]

    const dataListColumns = [
        {
            type: "default",
            bold: true,
            content: (value) => value.name
        },
        {
            type: "default",
            bold: true,
            content: (value) => (
                <div className="flex items-center space-x-2">
                    {/* nilai sort order */}
                    <span className="w-8 text-center">
                        {value.sort_order}
                    </span>

                    {/* tombol up/down */}
                    <div className="flex flex-col space-y-1">
                        <button
                            type="button"
                            onClick={() => handleChangeSortOrder(value, "up")}
                            className="flex items-center justify-center w-6 h-6 text-[10px] border rounded-full hover:bg-neutral-100 active:scale-95"
                            title="Move up"
                        >
                            ▲
                        </button>
                        <button
                            type="button"
                            onClick={() => handleChangeSortOrder(value, "down")}
                            className="flex items-center justify-center w-6 h-6 text-[10px] border rounded-full hover:bg-neutral-100 active:scale-95"
                            title="Move down"
                        >
                            ▼
                        </button>
                    </div>
                </div>
            ),
        },
        ...configuration('hierarchy').find((row) => row.key === 'product-line').value ? [{
            type: "default",
            bold: true,
            content: (value) => value.product_line?.name
        }] : [],
        {
            type: "default",
            bold: false,
            content: (value) => moment(value.created_at).format('MMMM D, YYYY')
        },
        {
            type: "default",
            bold: false,
            content: (value) => moment(value.updated_at).format('MMMM D, YYYY')
        },
        {
            type: "action",
            content: (value) => (
                <>
                    {can('product-type.update') && (
                        <Link to={`${value.id}/edit`} state={{ back: title, from: location?.pathname, transition: 'slide' }} className="inline-flex items-center p-1 text-white transition rounded-full bg-neutral-800 active:hover:scale-90">
                            <Pencil className="w-6 h-6" />
                        </Link>
                    )}

                    {can('product-type.delete') && (
                        <Delete data={value} onSuccess={() => mutateProductTypeData()} />
                    )}
                </>
            )
        }
    ]

    const handleChangeSortOrder = async (item, direction) => {
        try {
            // TODO: sesuaikan URL dengan API kamu
            await axios.post(`${process.env.REACT_APP_INVENTORY_API_URL}/product-types/${item.id}/sort-order`, {
                direction,
            })

            // reload data list
            mutateProductTypeData()
        } catch (error) {
            console.error("Failed to change sort order", error)
            // optionally tampilkan toast / alert
        }
    }


    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between text-xs">
                <div className="flex items-center sm:space-x-2">
                    <div className="relative hidden sm:block">
                        <div className="absolute inset-y-0 flex items-center pl-3 pointer-events-none">
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" width={24} height={24} viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                <circle cx={10} cy={10} r={7}></circle>
                                <line x1={21} y1={21} x2={15} y2={15}></line>
                            </svg>
                        </div>
                        <input ref={searchInput} onChange={(e) => setSearchParams({ search: e.target.value })} value={searchParams.get('search')} type="text" placeholder={`${lang.search} Product Type`} autoComplete="off" className="w-64 py-3 pl-8 pr-4 text-xs transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200" />
                    </div>
                    <Filter onSubmit={updateFilter} onRemove={removeFilter} data={Object.fromEntries(Object.entries({ name, from, to }).filter(([_, v]) => v != null))} />
                </div>
                <div className="flex items-center space-x-2">
                    {can('product-type.create') && (
                        <div>
                            <Link to="create" onClick={() => { setTransition({ type: 'slide', reverse: true }) }} state={{ back: title, from: location?.pathname, transition: { type: 'slide' } }} className="inline-flex items-center px-4 py-3 space-x-2 text-white transition bg-neutral-800 rounded-xl active:hover:scale-90">
                                <Plus className="w-4 h-4" stroke={1.5} />
                                <span>{lang.create}</span>
                            </Link>
                        </div>
                    )}
                    <button
                    type="button"
                    onClick={() => setIsGroupModalOpen(true)}
                    className="inline-flex items-center px-4 py-3 space-x-2 transition border border-neutral-200 rounded-xl hover:bg-neutral-50 active:hover:scale-90"
                    >
                    <Plus className="w-4 h-4" stroke={1.5} />
                    <span>Buat Group</span>
                    </button>

                </div>
            </div>
            <Table isLoading={isLoadingProductTypeData} data={productTypeData?.data ?? []} headers={dataListHeaders} columns={dataListColumns} key={(value) => value.id} isEmpty={productTypeData?.data.length === 0 && !search && !isLoadingProductTypeData} isEmptyOnSearching={productTypeData?.data.length === 0 && search && !isLoadingProductTypeData} toggleSort={toggleSort} orderBy={orderBy} direction={direction} />
            <Pagination links={productTypeData?.links} from={productTypeData?.from} to={productTypeData?.to} total={productTypeData?.total} />
            <GroupingModal
            open={isGroupModalOpen}
            onClose={() => setIsGroupModalOpen(false)}
            apiBaseUrl={process.env.REACT_APP_INVENTORY_API_URL}
            fallbackOptions={productTypeData?.data ?? []}
            />

        </div>
    )
}

function GroupingModal({ open, onClose, apiBaseUrl, fallbackOptions = [] }) {
  const [name, setName] = useState("")
  const [selectedChildIds, setSelectedChildIds] = useState([]) // ordered
  const [childSearch, setChildSearch] = useState("")
  const [errors, setErrors] = useState({})
  const [isSaving, setIsSaving] = useState(false)

  const [options, setOptions] = useState([])
  const [isLoadingOptions, setIsLoadingOptions] = useState(false)

  // Load options when modal opens (tries API; falls back to current page data)
  useEffect(() => {
    if (!open) return

    let cancelled = false

    const load = async () => {
      setIsLoadingOptions(true)
      try {
        // Adjust params to match your API if needed
        const res = await axios.get(`${apiBaseUrl}/product-types`, {
          params: { per_page: 1000, order_by: "name", direction: "asc" },
        })

        const list = res?.data?.data ?? res?.data ?? []
        if (!cancelled) setOptions(Array.isArray(list) ? list : [])
      } catch (e) {
        if (!cancelled) setOptions(fallbackOptions)
      } finally {
        if (!cancelled) setIsLoadingOptions(false)
      }
    }

    load()

    return () => {
      cancelled = true
    }
  }, [open, apiBaseUrl])

  // Reset form when opening
  useEffect(() => {
    if (!open) return
    setName("")
    setSelectedChildIds([])
    setChildSearch("")
    setErrors({})
    setIsSaving(false)
  }, [open])

  const cleanOptions = useMemo(() => {
    // Hide deleted records if your API has record_status
    return (options?.length ? options : fallbackOptions).filter((x) => x?.record_status !== "D")
  }, [options, fallbackOptions])

  const filteredChildOptions = useMemo(() => {
    const q = (childSearch || "").trim().toLowerCase()
    return cleanOptions
      .filter((x) => x?.id && x?.name)
      .filter((x) => (q ? String(x.name).toLowerCase().includes(q) : true))
  }, [cleanOptions, childSearch])

  const validate = () => {
    const next = {}

    if (!name.trim()) next.name = "Nama group wajib diisi."
    if (selectedChildIds.length === 0) next.items = "Minimal pilih 1 child."
    if (selectedChildIds.length > 4) next.items = "Maksimal 4 child."

    setErrors(next)
    return Object.keys(next).length === 0
  }

  const toggleChild = (id) => {
    setErrors((prev) => ({ ...prev, items: undefined }))

    setSelectedChildIds((prev) => {
      const exists = prev.includes(id)
      if (exists) return prev.filter((x) => x !== id)

      if (prev.length >= 4) {
        setErrors((e) => ({ ...e, items: "Maksimal 4 child." }))
        return prev
      }

      return [...prev, id] // selection order becomes sort_order
    })
  }

  const moveChild = (id, dir) => {
    setSelectedChildIds((prev) => {
      const idx = prev.indexOf(id)
      if (idx < 0) return prev

      const next = [...prev]
      const swapWith = dir === "up" ? idx - 1 : idx + 1
      if (swapWith < 0 || swapWith >= next.length) return prev

      ;[next[idx], next[swapWith]] = [next[swapWith], next[idx]]
      return next
    })
  }

  const selectedChildren = useMemo(() => {
    const map = new Map(cleanOptions.map((x) => [x.id, x]))
    return selectedChildIds
      .map((id) => map.get(id))
      .filter(Boolean)
  }, [cleanOptions, selectedChildIds])

  const handleSubmit = async () => {
    if (!validate()) return

    setIsSaving(true)
    try {
      const payload = {
        name: name.trim(),
        items: selectedChildIds.map((id, idx) => ({
            product_type_id: id,
            sort_order: idx + 1,
        })),
        }

      await axios.post(`${apiBaseUrl}/product-type-groups`, payload)

      onClose()
      // Optional: toast/alert
      // alert("Group berhasil dibuat")
    } catch (err) {
      console.error(err)
      const msg =
        err?.response?.data?.message ||
        "Gagal membuat group. Periksa endpoint / payload dan coba lagi."
      setErrors((e) => ({ ...e, submit: msg }))
    } finally {
      setIsSaving(false)
    }
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40"
        onClick={isSaving ? undefined : onClose}
      />

      {/* Modal */}
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="w-full max-w-3xl bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b">
            <div className="space-y-0.5">
              <div className="text-sm font-semibold">Buat Group Product Type</div>
              <div className="text-xs text-neutral-500">
                Pilih 1 parent dan maksimal 4 child.
              </div>
            </div>

            <button
              type="button"
              onClick={isSaving ? undefined : onClose}
              className="px-3 py-2 text-xs border rounded-xl hover:bg-neutral-50"
            >
              Tutup
            </button>
          </div>

          <div className="p-5 space-y-5">
            {errors.submit && (
              <div className="p-3 text-xs border rounded-xl border-red-200 bg-red-50 text-red-700">
                {errors.submit}
              </div>
            )}

            {/* Group Name */}
            <div className="space-y-1">
              <label className="text-xs font-medium">Nama Group</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                type="text"
                placeholder="Contoh: Frame Group"
                className="w-full px-4 py-3 text-xs transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200"
              />
              {errors.name && <div className="text-xs text-red-600">{errors.name}</div>}
            </div>
            

            {/* Children */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {/* Pick list */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-medium">Child Product Types</label>
                  <div className="text-xs text-neutral-500">
                    {selectedChildIds.length}/4
                  </div>
                </div>

                <input
                  value={childSearch}
                  onChange={(e) => setChildSearch(e.target.value)}
                  type="text"
                  placeholder="Cari child..."
                  className="w-full px-4 py-3 text-xs transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200"
                />

                <div className="border rounded-xl overflow-hidden">
                  <div className="max-h-64 overflow-auto divide-y">
                    {filteredChildOptions.map((pt) => {
                      const checked = selectedChildIds.includes(pt.id)
                      return (
                        <button
                          key={pt.id}
                          type="button"
                          onClick={() => toggleChild(pt.id)}
                          className="w-full flex items-center justify-between px-4 py-3 text-xs hover:bg-neutral-50 text-left"
                        >
                          <div className="flex items-center space-x-3">
                            <input
                              type="checkbox"
                              checked={checked}
                              readOnly
                              className="w-4 h-4"
                            />
                            <span className="font-medium">{pt.name}</span>
                          </div>
                          <span className="text-neutral-400">{pt.id}</span>
                        </button>
                      )
                    })}

                    {filteredChildOptions.length === 0 && (
                      <div className="px-4 py-6 text-xs text-neutral-500">
                        Tidak ada data.
                      </div>
                    )}
                  </div>
                </div>

                {errors.items && <div className="text-xs text-red-600">{errors.items}</div>}
              </div>

              {/* Selected + ordering */}
              <div className="space-y-2">
                <label className="text-xs font-medium">Urutan Child (Sort Order)</label>

                <div className="border rounded-xl overflow-hidden">
                  <div className="divide-y">
                    {selectedChildren.length === 0 && (
                      <div className="px-4 py-6 text-xs text-neutral-500">
                        Belum ada child dipilih.
                      </div>
                    )}

                    {selectedChildren.map((pt, idx) => (
                      <div key={pt.id} className="flex items-center justify-between px-4 py-3">
                        <div className="flex items-center space-x-3">
                          <div className="w-7 h-7 text-xs flex items-center justify-center rounded-full border">
                            {idx + 1}
                          </div>
                          <div className="text-xs">
                            <div className="font-medium">{pt.name}</div>
                            <div className="text-neutral-400">{pt.id}</div>
                          </div>
                        </div>

                        <div className="flex items-center space-x-2">
                          <button
                            type="button"
                            onClick={() => moveChild(pt.id, "up")}
                            disabled={idx === 0}
                            className="w-8 h-8 text-xs border rounded-xl hover:bg-neutral-50 disabled:opacity-40"
                            title="Move up"
                          >
                            ▲
                          </button>
                          <button
                            type="button"
                            onClick={() => moveChild(pt.id, "down")}
                            disabled={idx === selectedChildren.length - 1}
                            className="w-8 h-8 text-xs border rounded-xl hover:bg-neutral-50 disabled:opacity-40"
                            title="Move down"
                          >
                            ▼
                          </button>
                          <button
                            type="button"
                            onClick={() => toggleChild(pt.id)}
                            className="w-8 h-8 text-xs border rounded-xl hover:bg-neutral-50"
                            title="Remove"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="text-xs text-neutral-500">
                  Sort order akan disimpan sesuai urutan di sini.
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end px-5 py-4 border-t space-x-2">
            <button
              type="button"
              onClick={isSaving ? undefined : onClose}
              className="px-4 py-3 text-xs border rounded-xl hover:bg-neutral-50 disabled:opacity-50"
              disabled={isSaving}
            >
              Batal
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              className="inline-flex items-center px-4 py-3 text-xs text-white transition bg-neutral-800 rounded-xl active:hover:scale-90 disabled:opacity-50"
              disabled={isSaving}
            >
              {isSaving ? "Menyimpan..." : "Simpan Group"}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
